import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    home_size: "",
    address: "",
    preferred_date: null,
    special_notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  const serviceTypes = [
    { value: "standard", label: "Standard Cleaning" },
    { value: "deep", label: "Deep Cleaning" },
    { value: "movein", label: "Move-In Cleaning" },
    { value: "moveout", label: "Move-Out Cleaning" },
    { value: "recurring_weekly", label: "Recurring - Weekly" },
    { value: "recurring_biweekly", label: "Recurring - Biweekly" },
    { value: "recurring_monthly", label: "Recurring - Monthly" },
  ];

  const homeSizes = [
    { value: "small", label: "Small (Under 1,000 sq ft)" },
    { value: "medium", label: "Medium (1,000 - 2,000 sq ft)" },
    { value: "large", label: "Large (2,000 - 3,000 sq ft)" },
    { value: "xlarge", label: "Extra Large (Over 3,000 sq ft)" },
  ];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, "yyyy-MM-dd") : null,
      };

      await axios.post(`${API}/quotes`, payload);
      
      toast.success("Quote request submitted successfully! We'll contact you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        home_size: "",
        address: "",
        preferred_date: null,
        special_notes: "",
      });
      onClose();
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="quote-modal-overlay"
      onClick={onClose}
      data-testid="quote-modal-overlay"
    >
      <div
        className="quote-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="quote-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Request a Free Quote</h2>
            <p className="text-slate-600 mt-1">Fill out the form and we'll get back to you shortly</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name & Email Row */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="form-label">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="form-input"
                required
                data-testid="input-name"
              />
            </div>
            <div>
              <Label htmlFor="email" className="form-label">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="form-input"
                required
                data-testid="input-email"
              />
            </div>
          </div>

          {/* Phone & Service Type Row */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone" className="form-label">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="form-input"
                required
                data-testid="input-phone"
              />
            </div>
            <div>
              <Label className="form-label">
                Service Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => handleChange("service_type", value)}
              >
                <SelectTrigger className="form-input" data-testid="select-service">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Home Size & Date Row */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Home Size</Label>
              <Select
                value={formData.home_size}
                onValueChange={(value) => handleChange("home_size", value)}
              >
                <SelectTrigger className="form-input" data-testid="select-home-size">
                  <SelectValue placeholder="Select home size" />
                </SelectTrigger>
                <SelectContent>
                  {homeSizes.map((size) => (
                    <SelectItem key={size.value} value={size.value}>
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Preferred Date</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "form-input justify-start text-left font-normal",
                      !formData.preferred_date && "text-slate-500"
                    )}
                    data-testid="date-picker-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.preferred_date ? (
                      format(formData.preferred_date, "PPP")
                    ) : (
                      <span>Select a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.preferred_date}
                    onSelect={(date) => {
                      handleChange("preferred_date", date);
                      setDateOpen(false);
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    data-testid="calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="address" className="form-label">
              Service Address
            </Label>
            <Input
              id="address"
              type="text"
              placeholder="123 Main St, Houston, TX 77002"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              className="form-input"
              data-testid="input-address"
            />
          </div>

          {/* Special Notes */}
          <div>
            <Label htmlFor="notes" className="form-label">
              Special Notes or Requests
            </Label>
            <Textarea
              id="notes"
              placeholder="Any specific cleaning requirements or areas of focus..."
              value={formData.special_notes}
              onChange={(e) => handleChange("special_notes", e.target.value)}
              className="form-input min-h-[100px]"
              data-testid="input-notes"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            disabled={isSubmitting}
            data-testid="submit-quote-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Quote Request"
            )}
          </Button>

          <p className="text-center text-sm text-slate-500">
            By submitting, you agree to our{" "}
            <a href="/privacy-policy" className="text-sky-600 hover:underline">
              Privacy Policy
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default QuoteFormModal;
